import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  Upload,
  Images,
  Sparkles,
  Link2,
  ChevronDown,
  ChevronRight,
  X,
  Maximize2,
  ImageOff,
} from 'lucide-react'
import { Badge, Button, CodeBlock, DropdownMenu, FormSection, SearchField } from '../common'
import { ActivityIndicator } from '../common/feedback/ActivityIndicator'
import ImagePromptComposer from './ImagePromptComposer'
import { canResumeImageGenerationJob } from './imageGenerationJob'
import MediaUploadStatus from './MediaUploadStatus'
import { CLIENT_UPLOAD_PHASES } from './useMediaUploadQueue'
import MediaExternalImportPanel from './MediaExternalImportPanel'
import CaptionTracksEditor from './CaptionTracksEditor'
import { AI_ANALYSIS_DISCLAIMER, usePendingMediaReplacement } from './composerShared'
import { groupMediaAssetsByOwner, MEDIA_UPLOAD_ACCEPT } from './mediaAttachments'
import { assetAttachBlocked, assetModerationState } from '../moderation/assetModerationModel'
import { assetImageUrl } from '../../utils/mediaVariants'
import styles from './MediaSection.module.css'

const UNDERSCORE_PATTERN = /_/g
const UPPERCASE_LETTERS_PATTERN = /([A-Z])/g
const WHITESPACE_RUN_PATTERN = /\s+/g

// Media operations write into a concrete organization's media library, so the
// composer needs to know which library to touch. That is operation context, not
// post identity: it never reaches the post document, and it is deliberately
// rendered inside the media workflow rather than as a global "source" field.
const NO_MEDIA_ORGANIZATIONS = []
const LIBRARY_THUMBNAIL_WIDTH = 320
const MEDIA_ORGANIZATION_REQUIRED_HINT = 'Choose where this media is saved before uploading, importing, or generating.'
const IMAGE_GENERATION_NEEDS_MEDIA_ORGANIZATION_HINT = 'Choose where this media is saved above to enable image generation.'

const SOURCE_OPTIONS = [
  {
    id: 'upload',
    label: 'Upload file',
    icon: Upload,
    description: 'Upload an image or video from your device',
  },
  {
    id: 'library',
    label: 'Pick from library',
    icon: Images,
    description: 'Reuse a saved org asset',
  },
  {
    id: 'generate',
    label: 'Generate with AI',
    icon: Sparkles,
    description: 'Create imagery and captions from a prompt',
  },
  {
    id: 'external',
    label: 'Import from URL',
    icon: Link2,
    description: 'Pull in a video from YouTube, Instagram, TikTok, Drive, or X',
  },
]

function isVideoMedia(media) {
  return media?.type === 'video' || String(media?.type || '').startsWith('video/')
}

function humanizeSummaryKey(key) {
  return String(key)
    .replace(UNDERSCORE_PATTERN, ' ')
    .replace(UPPERCASE_LETTERS_PATTERN, ' $1')
    .replace(WHITESPACE_RUN_PATTERN, ' ')
    .trim()
}

function formatSummaryValue(value) {
  if (value === null || typeof value === 'undefined') return 'None'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function TranscriptionStatusChip({ job, onRetry, onReplace }) {
  if (!job) return null

  if (job.status === 'polling' || job.status === 'retrying') {
    return (
      <ActivityIndicator
        compact
        status={job.status}
        label={job.status === 'retrying' ? 'Retrying video analysis…' : 'Analyzing video…'}
        startedAt={job.startedAt}
      />
    )
  }

  if (job.status === 'completed') {
    return (
      <ActivityIndicator compact status="success" label="Video analysis complete" />
    )
  }

  if (job.status === 'failed_retryable') {
    return (
      <div className={`${styles.transcriptionChip} ${styles.transcriptionChipFailed}`}>
        <span className={styles.statusMessage}>{job.error || 'Video analysis could not finish.'}</span>
        <button type="button" className={styles.statusRetry} onClick={onRetry} disabled={!onRetry}>
          Retry
        </button>
      </div>
    )
  }

  if (job.status === 'invalid_input' || job.status === 'too_large') {
    return (
      <div className={`${styles.transcriptionChip} ${styles.transcriptionChipFailed}`}>
        <span className={styles.statusMessage}>{job.error}</span>
        <button type="button" className={styles.statusRetry} onClick={onReplace} disabled={!onReplace}>
          Replace
        </button>
      </div>
    )
  }

  if (job.status === 'unavailable') {
    return (
      <div className={`${styles.transcriptionChip} ${styles.transcriptionChipInfo}`}>
        {job.error || 'Video analysis is unavailable for this organization.'}
      </div>
    )
  }

  return null
}

function MediaSection({
  uploadedMedia,
  onMediaUpload,
  onRetryUpload,
  onCancelUpload,
  saveUploadsToLibrary = false,
  onSaveUploadsToLibraryChange,
  onRemoveMedia,
  libraryAssets,
  assetSearch,
  onAssetSearchChange,
  onAttachAsset,
  mediaOrganizations = NO_MEDIA_ORGANIZATIONS,
  mediaOrganizationId = '',
  onMediaOrganizationChange,
  onActivePanelChange,
  aiCapabilities,
  imageGenerationCapabilities = aiCapabilities,
  hasCaptionText,
  imagePrompt,
  imagePromptFields,
  onImagePromptChange,
  onImagePromptFieldChange,
  onBuildPromptFromFields,
  imagePromptPresets = [],
  selectedPromptPresetId = '',
  onPromptPresetChange,
  onSavePromptPreset,
  onSaveDefaultPromptPreset,
  promptPresetSaving,
  imagePending,
  imageStatus,
  onGenerateImage,
  onDiscardImage,
  // AI reference images are opt-in: the composer shows its upload button only
  // when `onAddReferenceImages` is passed.
  referenceImages,
  onAddReferenceImages,
  onRemoveReferenceImage,
  referenceImagesUploading,
  aiReview,
  onApplyTags,
  showTagSuggestionsInAnalysis = true,
  onPreviewImage,
  orgSlug,
  onExternalCaptionApply,
  ingestApi,
  isCryptoDotCom = false,
  transcriptionJobs = {},
  onRetryTranscription,
  // Caption authoring is opt-in: only composers that send each video's
  // caption tracks with the post pass these handlers.
  onUploadCaptionTrack,
  onChangeCaptionTracks,
}) {
  const [activePanel, setActivePanel] = useState(null)
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const [rawSummaryOpen, setRawSummaryOpen] = useState(false)
  const [pendingUploadFiles, setPendingUploadFiles] = useState([])
  const uploadInputRef = useRef(null)
  const uploadScopeId = useId()
  const { beginReplacement, consumeReplacement } = usePendingMediaReplacement((id) => {
    const index = uploadedMedia.findIndex((media) => media.id === id)
    if (index >= 0) onRemoveMedia?.(index)
  }, uploadInputRef)
  const captionsEnabled = Boolean(onUploadCaptionTrack && onChangeCaptionTracks)

  const requiresMediaOrganization = mediaOrganizations.length > 1
  const activeMediaOrganization = mediaOrganizations.find((organization) => organization.id === mediaOrganizationId)
    || (mediaOrganizations.length === 1 ? mediaOrganizations[0] : null)
  const mediaOrganizationMissing = requiresMediaOrganization && !activeMediaOrganization
  const mediaContextHintId = `${uploadScopeId}-media-context`
  const hasClientUploads = uploadedMedia.some((media) => CLIENT_UPLOAD_PHASES.has(media.uploadPhase))

  const openPanel = (panelId) => {
    setActivePanel((current) => (current === panelId ? null : panelId))
  }

  const openUploadPicker = () => {
    setActivePanel('upload')
    uploadInputRef.current?.click()
  }

  const handleUploadSelection = (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    consumeReplacement(files)
    if (files.length === 0) return
    if (mediaOrganizationMissing) {
      setPendingUploadFiles((currentFiles) => [...currentFiles, ...files])
      return
    }
    onMediaUpload?.(files)
  }

  const removePendingUploadFile = (index) => {
    setPendingUploadFiles((currentFiles) => currentFiles.filter((_, fileIndex) => fileIndex !== index))
  }

  const selectSource = (option) => {
    if (option.id === 'upload') {
      openUploadPicker()
      return
    }
    openPanel(option.id)
  }

  const libraryGroups = useMemo(
    () => groupMediaAssetsByOwner(libraryAssets, mediaOrganizations),
    [libraryAssets, mediaOrganizations],
  )
  // Generation is gated by the library it would write into: an unnamed library
  // is the real blocker, so say so and point at the picker rendered right above
  // instead of reporting a generic "AI is unavailable" dead end.
  const generationCapabilities = mediaOrganizationMissing
    ? {
      ...imageGenerationCapabilities,
      features: {
        ...imageGenerationCapabilities?.features,
        image_generation: { enabled: false, reason: { message: IMAGE_GENERATION_NEEDS_MEDIA_ORGANIZATION_HINT } },
      },
    }
    : imageGenerationCapabilities

  const mediaOrganizationContext = mediaOrganizations.length === 0 ? null : requiresMediaOrganization ? (
    <>
      <label className={styles.aiPresetSelectWrap}>
        <span>Save media to</span>
        <select
          className={styles.aiPresetSelect}
          value={activeMediaOrganization?.id || ''}
          aria-describedby={mediaContextHintId}
          onChange={(event) => onMediaOrganizationChange?.(event.target.value)}
        >
          <option value="">Select an organization</option>
          {mediaOrganizations.map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>
      </label>
      <p id={mediaContextHintId} className={styles.panelHelper} role="note">
        {mediaOrganizationMissing
          ? MEDIA_ORGANIZATION_REQUIRED_HINT
          : `Stored in the ${activeMediaOrganization.name} media library. This does not change who the post is from.`}
      </p>
    </>
  ) : (
    <p className={styles.panelHelper}>{`Saving media to ${mediaOrganizations[0].name}`}</p>
  )

  // A library card shows the asset's moderation state as the media library
  // does. Rejected media cannot go into a post while moderation enforces
  // the library, so its card is disabled and says why.
  const renderLibraryCard = (asset) => {
    const moderationState = assetModerationState(asset)
    const blocked = assetAttachBlocked(asset)
    const title = asset.title || 'media asset'
    return (
      <button
        key={asset.id}
        type="button"
        className={styles.libraryCard}
        aria-label={blocked
          ? `${title}: rejected by moderation, cannot be used`
          : asset.organizationName
            ? `Attach ${title} from ${asset.organizationName}`
            : `Attach ${title}`}
        disabled={blocked}
        aria-disabled={blocked ? 'true' : undefined}
        onClick={() => onAttachAsset(asset)}
      >
        <div className={styles.libraryThumb}>
          {asset.moderation?.quarantined ? (
            <div className={styles.libraryThumbUnavailable} role="img" aria-label={`Preview unavailable for ${title}`}>
              <ImageOff size={20} aria-hidden="true" />
              <span>Preview unavailable</span>
            </div>
          ) : asset.asset_type === 'video' ? (
            <video src={asset.url} muted className={styles.previewVideo} />
          ) : (
            <img
              src={assetImageUrl(asset, LIBRARY_THUMBNAIL_WIDTH)}
              alt={asset.title}
              className={styles.previewImage}
              onError={(event) => {
                // A missing variant falls back to the original once.
                if (asset.url && event.currentTarget.getAttribute('src') !== asset.url) event.currentTarget.src = asset.url
              }}
            />
          )}
        </div>
        <div className={styles.libraryBody}>
          <strong>{asset.title}</strong>
          {moderationState ? (
            <div className={styles.libraryState}>
              <Badge compact tone={moderationState.tone} title={moderationState.description}>{moderationState.label}</Badge>
            </div>
          ) : null}
          <span>
            {blocked
              ? 'Rejected by moderation. It cannot be used in a post.'
              : Array.isArray(asset.organization_tags)
                ? (asset.organization_tags.length > 0
                  ? `Used by ${asset.organization_tags.map((organization) => organization.name).join(', ')}`
                  : 'Not used yet')
                : asset.source_kind === 'post_media' ? 'From posts' : 'Library'}
          </span>
        </div>
      </button>
    )
  }

  const activeSource = SOURCE_OPTIONS.find((option) => option.id === activePanel)
  const attachedCountText = `${uploadedMedia.length} ${uploadedMedia.length === 1 ? 'file' : 'files'} attached`
  const hasReview = Boolean(
    aiReview?.transcript
      || (showTagSuggestionsInAnalysis && aiReview?.tags && aiReview.tags.length > 0)
      || aiReview?.betSummary
  )
  const hasPollingTranscription = Object.values(transcriptionJobs).some((job) => (
    job?.status === 'polling' || job?.status === 'retrying'
  ))
  const showAiAnalysis = hasReview || hasPollingTranscription
  const showPendingAnalysis = hasPollingTranscription && !hasReview
  const imageGenerationStatus = imageStatus?.status
  const imageGenerationNeedsAttention = (
    canResumeImageGenerationJob(imageStatus)
    || imageGenerationStatus === 'pending'
    || imageGenerationStatus === 'processing'
    || imageGenerationStatus === 'failed'
  )
  const shouldExposeMedia = uploadedMedia.length > 0 || showAiAnalysis || imageGenerationNeedsAttention

  useEffect(() => {
    if (!imageGenerationNeedsAttention) return
    setActivePanel((current) => current || 'generate')
  }, [imageGenerationNeedsAttention, imageGenerationStatus])

  useEffect(() => {
    onActivePanelChange?.(activePanel)
  }, [activePanel, onActivePanelChange])

  useEffect(() => {
    if (mediaOrganizationMissing || pendingUploadFiles.length === 0) return
    const files = pendingUploadFiles
    setPendingUploadFiles([])
    onMediaUpload?.(files)
  }, [mediaOrganizationMissing, onMediaUpload, pendingUploadFiles])

  return (
    <FormSection
      title={shouldExposeMedia ? 'Media' : 'Add media (optional)'}
      headingSize="subsection"
      description={shouldExposeMedia ? 'Upload, choose, generate, or import an image or video.' : undefined}
      collapsible={!shouldExposeMedia}
      defaultExpanded
      className={!shouldExposeMedia ? styles.optionalMediaDisclosure : undefined}
    >
      <div className={styles.mediaSection}>
        <input
          ref={uploadInputRef}
          type="file"
          accept={MEDIA_UPLOAD_ACCEPT}
          multiple
          onChange={handleUploadSelection}
          aria-label="Upload media"
          className={styles.hiddenFileInput}
        />
        {hasClientUploads ? (
          <p className={styles.uploadWarning} role="note">
            Keep this window open until your files are ready. Leaving now can interrupt uploads or lose the result.
          </p>
        ) : null}
        {uploadedMedia.length > 0 ? (
          <div className={styles.mediaGallery}>
            {uploadedMedia.map((media, index) => {
              const transcriptionJob = isVideoMedia(media) ? transcriptionJobs[media.id] : null
              // Media attached from the library keeps its moderation state.
              const attachedModerationState = assetModerationState(media)
              if (media.uploadPhase && (media.uploadPhase !== 'ready' || ['polling', 'retrying'].includes(transcriptionJob?.status))) {
                return (
                  <MediaUploadStatus
                    key={media.id}
                    media={media}
                    job={transcriptionJob}
                    onRetry={() => onRetryUpload?.(media.id)}
                    onCancel={() => onCancelUpload?.(media.id)}
                    onRemove={() => onRemoveMedia(index)}
                    onReplace={() => { beginReplacement(media.id); openUploadPicker() }}
                  />
                )
              }
              return (
                <div key={media.id} className={styles.mediaCard}>
                  <div className={styles.mediaPreview}>
                    {media.type === 'image' ? (
                      <button
                        type="button"
                        className={styles.imagePreviewWrap}
                        onClick={() => onPreviewImage?.(media.previewUrl)}
                      >
                        <img
                          src={media.previewUrl}
                          alt={media.name}
                          className={styles.previewImage}
                        />
                        <span className={styles.expandIcon}>
                          <Maximize2 size={14} />
                        </span>
                      </button>
                    ) : (
                      <div className={styles.videoPreviewWrap}>
                        {media.posterUrl ? (
                          <img
                            src={media.posterUrl}
                            alt={media.name}
                            className={styles.previewImage}
                          />
                        ) : media.previewUrl || media.url ? (
                          <video
                            // #t=0.001 nudges browsers that stop at metadata to
                            // paint the first frame (same convention as the
                            // widget's card media).
                            src={`${media.previewUrl || media.url}#t=0.001`}
                            className={styles.previewVideo}
                            preload="metadata"
                            muted
                            playsInline
                          />
                        ) : null}
                      </div>
                    )}
                    {media.uploading ? (
                      <div className={styles.uploadOverlay}>
                        <ActivityIndicator
                          compact
                          status="running"
                          label={`Uploading ${media.name}${Number.isFinite(media.uploadProgress) ? `: ${media.uploadProgress}%` : ''}`}
                          className={styles.uploadActivity}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.mediaCardFooter}>
                    <span className={styles.mediaName}>{media.name}</span>
                    {attachedModerationState ? (
                      <Badge compact tone={attachedModerationState.tone} title={attachedModerationState.description}>{attachedModerationState.label}</Badge>
                    ) : null}
                    <button
                      type="button"
                      className={styles.mediaRemove}
                      onClick={() => onRemoveMedia(index)}
                      aria-label={`Remove ${media.name}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {transcriptionJob ? (
                    <div className={styles.transcriptionStatus}>
                      <TranscriptionStatusChip
                        job={transcriptionJob}
                        onRetry={transcriptionJob.status === 'failed_retryable' && transcriptionJob.retryable
                          ? () => onRetryTranscription?.(media.id)
                          : null}
                        onReplace={transcriptionJob.status === 'invalid_input' || transcriptionJob.status === 'too_large'
                          ? () => { beginReplacement(media.id); openUploadPicker() }
                          : null}
                      />
                    </div>
                  ) : null}
                  {captionsEnabled && isVideoMedia(media) && !media.uploading ? (
                    <CaptionTracksEditor
                      mediaName={media.name}
                      tracks={media.captionTracks || []}
                      uploading={Boolean(media.captionUploading)}
                      onUploadFile={(file) => onUploadCaptionTrack(media, file)}
                      onChange={(tracks) => onChangeCaptionTracks(media, tracks)}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : null}

        <div className={styles.addMediaBar}>
          <DropdownMenu
            align="start"
            triggerLabel="Add media sources"
            trigger={(
              <Button type="button" size="lg">
                Add media
                <ChevronDown size={16} className={styles.addMediaChevron} aria-hidden="true" />
              </Button>
            )}
            variant="rich"
            selectionMode="single"
            items={SOURCE_OPTIONS.map((option) => ({
              key: option.id,
              label: option.label,
              description: option.description,
              icon: option.icon,
              selected: activePanel === option.id,
              onSelect: () => selectSource(option),
            }))}
          />

          {activeSource ? (
            <div className={styles.activeSourceChip}>
              <span>{activeSource.label}</span>
              <button
                type="button"
                className={styles.activeSourceClose}
                onClick={() => setActivePanel(null)}
                aria-label="Close panel"
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
        </div>

        {activePanel === 'upload' ? (
          <div className={`${styles.panel} ${styles.uploadPanel}`}>
            <div className={styles.uploadPanelDetails}>
              {mediaOrganizationContext}
              <div className={styles.uploadPanelSummary}>
                <span className={styles.uploadPanelCount}>
                  {pendingUploadFiles.length > 0
                    ? `${pendingUploadFiles.length} ${pendingUploadFiles.length === 1 ? 'file' : 'files'} selected`
                    : attachedCountText}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={openUploadPicker}
                >
                  {uploadedMedia.length === 0 ? 'Choose files' : 'Add more files'}
                </Button>
              </div>
              {pendingUploadFiles.length > 0 ? (
                <ul className={styles.pendingUploadList} aria-label="Files waiting to upload" aria-live="polite">
                  {pendingUploadFiles.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                      className={styles.pendingUploadItem}
                    >
                      <span className={styles.pendingUploadName}>{file.name}</span>
                      <span className={styles.pendingUploadStatus}>Waiting for organization</span>
                      <button
                        type="button"
                        className={styles.pendingUploadRemove}
                        onClick={() => removePendingUploadFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className={styles.uploadSupport}>Images up to 25 MB and videos up to 200 MB.</p>
            </div>
            {onSaveUploadsToLibraryChange ? (
              <label className={styles.uploadLibraryToggle}>
                <input
                  type="checkbox"
                  checked={saveUploadsToLibrary}
                  aria-label="Save uploads to library"
                  aria-describedby={uploadScopeId}
                  onChange={(event) => onSaveUploadsToLibraryChange(event.target.checked)}
                />
                <span className={styles.uploadLibraryLabel}>
                  <span>Save uploads to library</span>
                  <span id={uploadScopeId} className={styles.uploadLibraryScope}>For files you add next</span>
                </span>
              </label>
            ) : null}
          </div>
        ) : null}

        {activePanel === 'library' ? (
          <div className={styles.panel}>
            <h4 className={styles.reviewTitle}>Media library</h4>
            <SearchField
              value={assetSearch}
              onChange={onAssetSearchChange}
              placeholder="Search library..."
              label="Search library"
              clearLabel="Clear"
              form={false}
            />
            {requiresMediaOrganization ? (
              libraryGroups.length === 0 ? (
                <p className={styles.emptyHint}>No saved assets match.</p>
              ) : (
                libraryGroups.map((group) => (
                  <div
                    key={group.organizationId}
                    role="group"
                    aria-label={`${group.organizationName} media library`}
                  >
                    <p className={styles.panelHelper}>{group.organizationName}</p>
                    <div className={styles.libraryGrid}>
                      {group.assets.map(renderLibraryCard)}
                    </div>
                  </div>
                ))
              )
            ) : (
              <div className={styles.libraryGrid}>
                {libraryAssets.slice(0, 12).map(renderLibraryCard)}
                {libraryAssets.length === 0 ? (
                  <p className={styles.emptyHint}>No saved assets match.</p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {activePanel === 'generate' ? (
          <>
            {mediaOrganizationContext ? (
              <div className={styles.panel}>
                {mediaOrganizationContext}
                {requiresMediaOrganization && activeMediaOrganization ? (
                  <p className={styles.panelHelper} role="note">
                    {`Generated images are created in the ${activeMediaOrganization.name} media library and attached as ${activeMediaOrganization.name} media.`}
                  </p>
                ) : null}
              </div>
            ) : null}
            <ImagePromptComposer
              imagePrompt={imagePrompt}
              imagePromptFields={imagePromptFields}
              onImagePromptChange={onImagePromptChange}
              onImagePromptFieldChange={onImagePromptFieldChange}
              onBuildPromptFromFields={onBuildPromptFromFields}
              imagePromptPresets={imagePromptPresets}
              selectedPromptPresetId={selectedPromptPresetId}
              onPromptPresetChange={onPromptPresetChange}
              onSavePromptPreset={onSavePromptPreset}
              onSaveDefaultPromptPreset={onSaveDefaultPromptPreset}
              promptPresetSaving={promptPresetSaving}
              imagePending={imagePending}
              imageStatus={imageStatus}
              onGenerateImage={onGenerateImage}
              onDiscardImage={onDiscardImage}
              aiCapabilities={generationCapabilities}
              referenceImages={referenceImages}
              onAddReferenceImages={onAddReferenceImages}
              onRemoveReferenceImage={onRemoveReferenceImage}
              referenceImagesUploading={referenceImagesUploading}
            />
          </>
        ) : null}

        {activePanel === 'external' ? (
          <>
            {mediaOrganizationContext ? (
              <div className={styles.panel}>{mediaOrganizationContext}</div>
            ) : null}
            {mediaOrganizationMissing ? null : (
              <MediaExternalImportPanel
                orgSlug={orgSlug}
                onImported={onAttachAsset}
                onSuggestCaption={onExternalCaptionApply}
                postTextEmpty={!hasCaptionText}
                ingestApi={ingestApi}
              />
            )}
          </>
        ) : null}

        {showAiAnalysis ? (
          <div className={styles.aiAnalysisDisclosure}>
            <button
              type="button"
              className={styles.aiAnalysisHeader}
              onClick={() => setAnalysisOpen((current) => !current)}
              aria-expanded={analysisOpen}
            >
              <span className={styles.aiAnalysisTitle}>
                {analysisOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                AI analysis
              </span>
              {showPendingAnalysis ? (
                <ActivityIndicator compact status="polling" label="Transcribing video…" />
              ) : null}
            </button>
            {analysisOpen ? (
              <div className={styles.aiAnalysisBody}>
                <p className={styles.aiDisclaimer}>{AI_ANALYSIS_DISCLAIMER}</p>
                {aiReview.transcript ? (
                  <div className={styles.reviewCard}>
                    <strong>Transcript</strong>
                    <p>{aiReview.transcript}</p>
                  </div>
                ) : null}
                {showTagSuggestionsInAnalysis && aiReview.tags && aiReview.tags.length > 0 ? (
                  <div className={styles.reviewCard}>
                    <strong>Suggested tags</strong>
                    <p>{aiReview.tags.join(', ')}</p>
                    <Button type="button" size="sm" onClick={onApplyTags}>
                      Apply matching tags
                    </Button>
                  </div>
                ) : null}
                {aiReview.betSummary ? (
                  <div className={styles.reviewCard}>
                    <strong>{isCryptoDotCom ? 'Extracted contract details' : 'Extracted bet details'}</strong>
                    <dl className={styles.reviewDetails}>
                      {Object.entries(aiReview.betSummary).map(([key, value]) => (
                        <div key={key}>
                          <dt>{humanizeSummaryKey(key)}</dt>
                          <dd>{formatSummaryValue(value)}</dd>
                        </div>
                      ))}
                    </dl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="compact"
                      onClick={() => setRawSummaryOpen((current) => !current)}
                    >
                      {rawSummaryOpen ? 'Hide raw JSON' : 'Show raw JSON'}
                    </Button>
                    {rawSummaryOpen ? (
                      <CodeBlock value={aiReview.betSummary} label="Extracted bet details" language="json" />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </FormSection>
  )
}

export default MediaSection
